import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/student/PageHeader";
import { Colors, useLayoutCommon } from "@/contexts/LayoutContext";
import { Button } from "@/components/ui";
import { delay } from "@/lib/utils";
import { Play, Clock, Target, Award, FastForward } from "lucide-react";
import { useLevelTestContext } from "@/contexts/LevelTestContext";
import { apiSkipLevelTest } from "@/services/student/level-test.api";
import { usePrivate } from "@/contexts/PrivateContext";
import { LevelTestStates } from "@/services/student/level-test.interfaces";

export const WelcomeTest: React.FC = () => {
  const { setBackgroundColor } = useLayoutCommon();
  const navigate = useNavigate();
  const { updateUser } = usePrivate();
  const [isStarting, setIsStarting] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const { actions } = useLevelTestContext();

  useEffect(() => {
    setBackgroundColor(Colors.WHITE);
  }, [setBackgroundColor]);

  const handleStartTest = async () => {
    setIsStarting(true);
    try {
      // Resetar o teste para garantir estado limpo
      console.log('🔄 Resetting level test before starting...');
      actions.resetTest();
      
      // Simular inicialização do teste
      await delay(500);
      navigate('/student/level-test/test');
    } catch (error) {
      console.error('Erro ao iniciar teste:', error);
      setIsStarting(false);
    }
  };

  const handleSkipTest = async () => {
    setIsSkipping(true);
    try {
      await apiSkipLevelTest();
      updateUser({ levelTestState: LevelTestStates.Skipped });
      actions.resetTest();
      navigate("/student/dashboard", { replace: true });
    } catch (error) {
      console.error("Erro ao pular teste:", error);
      setIsSkipping(false);
    }
  };

  return (
    <div className="max-w-[1800px] w-full">
      <PageHeader
        showAvatar={true}
        showActions={true}
        title="Teste de Nivelamento"
        subtitle="Descubra seu nível de inglês e receba recomendações personalizadas"
      />

      <div className="w-full mt-6">
        {/* Banner com imagem de fundo */}
        <div className="relative overflow-hidden rounded-t-2xl">
          <div 
            className="relative w-full py-24 px-8"
            style={{
              backgroundImage: `linear-gradient(rgba(26, 26, 26, 0.85), rgba(26, 26, 26, 0.85)), url('https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          >
            {/* Padrão de sobreposição escura */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#1A1A1A]/40 to-[#2A2A2A]/60"></div>
            
            {/* Elementos decorativos geométricos */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
              <div className="absolute -top-20 -right-20 w-80 h-80 bg-gradient-to-br from-gray-600/10 to-transparent rounded-full blur-3xl"></div>
              <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-gradient-to-tr from-gray-500/10 to-transparent rounded-full blur-3xl"></div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-white/3 to-transparent rounded-full"></div>
            </div>
            
            {/* Conteúdo */}
            <div className="relative z-10 max-w-4xl mx-auto text-center">
              <div className="mb-6">
                <span className="inline-flex items-center px-4 py-2 bg-white/15 backdrop-blur-sm rounded-full text-white/95 text-sm font-medium border border-white/20">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Teste adaptativo personalizado
                </span>
              </div>
              
              <h2 className="text-5xl font-bold text-white mb-8 leading-tight drop-shadow-lg">
                Bem-vindo ao Teste de Nivelamento
              </h2>
              
              {/* Botão CTA no banner */}
              <div className="mb-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
                <Button
                  onClick={handleStartTest}
                  disabled={isStarting || isSkipping}
                  className="px-10 py-5 text-lg font-semibold bg-white text-gray-900 hover:bg-gray-100 disabled:opacity-50 shadow-lg hover:shadow-xl transform transition-all duration-200 hover:-translate-y-0.5 border-2 border-white/20"
                >
                  <div className="flex items-center gap-3">
                    {isStarting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                        Iniciando Teste...
                      </>
                    ) : (
                      <>
                        <Play className="w-6 h-6" />
                        Iniciar Teste de Nivelamento
                      </>
                    )}
                  </div>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSkipTest}
                  disabled={isStarting || isSkipping}
                  className="px-8 py-5 text-base font-medium border-2 border-white/40 bg-white/10 text-white backdrop-blur-sm hover:bg-white/20 disabled:opacity-50"
                >
                  <div className="flex items-center gap-2">
                    {isSkipping ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                        Aplicando…
                      </>
                    ) : (
                      <>
                        <FastForward className="w-5 h-5" />
                        Começar pelos primeiros módulos (pular teste)
                      </>
                    )}
                  </div>
                </Button>
              </div>
              
              <p className="text-xl text-white/95 max-w-4xl mx-auto leading-relaxed drop-shadow">
                Este teste de nivelamento é adequado para todos os níveis. Alunos iniciantes
                terminarão mais cedo, enquanto alunos mais avançados responderão a mais questões,
                permitindo uma avaliação mais precisa. O teste tem entre 30 e 60 questões e dura
                de 10 a 20 minutos.
              </p>
              
              {/* Indicadores visuais */}
              <div className="flex justify-center gap-8 mt-10">
                <div className="flex items-center gap-2 text-white/90">
                  <Clock className="w-5 h-5" />
                  <span className="text-sm font-medium">10 a 20 minutos</span>
                </div>
                <div className="flex items-center gap-2 text-white/90">
                  <Target className="w-5 h-5" />
                  <span className="text-sm font-medium">Adaptativo</span>
                </div>
                <div className="flex items-center gap-2 text-white/90">
                  <Award className="w-5 h-5" />
                  <span className="text-sm font-medium">Certificado</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-b-2xl px-6 pt-0 pb-6">
          <div className="w-[1227px] mx-auto">
            <div className="max-w-4xl mx-auto">

            {/* Mensagem informativa */}
            <div className="text-center py-6">
              <p className="text-sm text-gray-600 font-medium">
                <svg className="w-4 h-4 inline-block mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Você pode pausar e retomar o teste a qualquer momento
              </p>
            </div>

            {/* Cards informativos */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mb-4">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Duração Estimada
                </h3>
                <p className="text-gray-600">
                  De 10 a 20 minutos, com entre 30 e 60 questões conforme o seu desempenho
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mb-4">
                  <Target className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Níveis Avaliados
                </h3>
                <p className="text-gray-600">
                  De básico até avançado (A1-C2)
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg mb-4">
                  <Award className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Resultado Personalizado
                </h3>
                <p className="text-gray-600">
                  Recomendações específicas para seu aprendizado
                </p>
              </div>
            </div>

            {/* Seção explicativa */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Como funciona o teste?
              </h3>
              <div className="space-y-3 text-gray-700">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-6 h-6 bg-blue-100 rounded-full text-sm font-medium text-blue-600 mt-0.5">
                    1
                  </div>
                  <p>
                    <strong>Questões progressivas:</strong> Começamos com questões mais fáceis
                    e aumentamos a dificuldade conforme você avança.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-6 h-6 bg-blue-100 rounded-full text-sm font-medium text-blue-600 mt-0.5">
                    2
                  </div>
                  <p>
                    <strong>Múltipla escolha:</strong> Todas as questões são de múltipla escolha
                    com 4 opções de resposta.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-6 h-6 bg-blue-100 rounded-full text-sm font-medium text-blue-600 mt-0.5">
                    3
                  </div>
                  <p>
                    <strong>Sem tempo limite:</strong> Responda no seu ritmo, sem pressão de tempo.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-6 h-6 bg-blue-100 rounded-full text-sm font-medium text-blue-600 mt-0.5">
                    4
                  </div>
                  <p>
                    <strong>Avaliação imediata:</strong> Receba seu resultado assim que terminar.
                  </p>
                </div>
              </div>
            </div>

            {/* Dicas importantes */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-amber-900 mb-3">
                💡 Dicas para um melhor resultado
              </h3>
              <ul className="space-y-2 text-amber-800">
                <li>• Leia atentamente cada questão antes de responder</li>
                <li>• Não há respostas erradas - o teste adapta-se ao seu nível</li>
                <li>• Seja honesto - isso garante recomendações mais precisas</li>
                <li>• Responda baseado no seu conhecimento atual</li>
              </ul>
            </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
